import { useState } from "react";
import USPhoneInput from "./USPhoneInput";

const inputClass =
  "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200 placeholder-gray-400 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";
const labelClass = "block font-constantia text-black-deep mb-1.5 text-sm";
const reqMark = <span className="text-red-500 ml-0.5">*</span>;

const ENDPOINTS = {
  consultation:     "/api/consultations",
  coi:              "/api/service-requests",
  "policy-change":  "/api/service-requests",
  "contact-update": "/api/service-requests",
  claim:            "/api/service-requests",
  "call-request":   "/api/service-requests",
};

const CHANGE_TYPES   = ["Add / Remove Driver", "Change Address", "Change Coverage", "Add / Remove Vehicle", "Other"];
const LOSS_TYPES     = ["Auto Accident", "Property Damage", "Theft", "Weather / Natural Disaster", "Liability", "Other"];
const UPDATE_OPTIONS = ["Phone Number", "Email Address", "Mailing Address", "Named Insured", "Other"];
const CALL_TIMES     = ["Morning (9 am – 12 pm)", "Afternoon (12 pm – 3 pm)", "Late Afternoon (3 pm – 5 pm)"];
const DELIVERY_OPTS  = ["Email", "Fax", "Mail"];
const YES_NO         = ["Yes", "No"];

export default function QuickActionModal({ action, onClose }) {
  const [form, setForm]           = useState({ fieldsToUpdate: [] });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "fieldsToUpdate") {
      setForm((prev) => ({
        ...prev,
        fieldsToUpdate: checked
          ? [...(prev.fieldsToUpdate || []), value]
          : (prev.fieldsToUpdate || []).filter((v) => v !== value),
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const endpoint = ENDPOINTS[action.key];
      const payload =
        action.key === "consultation"
          ? { fullName: form.fullName, email: form.email, address: form.address, phone: form.phone, details: form.details }
          : { requestType: action.key, ...form };

      const res  = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reusable field builders ──────────────────────────────────────────
  const field = (name, label, type = "text", placeholder = "", required = false) => (
    <div>
      <label className={labelClass}>{label}{required && reqMark}</label>
      <input
        type={type} name={name} required={required}
        placeholder={placeholder} value={form[name] || ""}
        onChange={handleChange} className={inputClass}
      />
    </div>
  );

  const select = (name, label, options, required = false) => (
    <div>
      <label className={labelClass}>{label}{required && reqMark}</label>
      <select name={name} value={form[name] || ""} onChange={handleChange}
        className={inputClass + " cursor-pointer"}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const textarea = (name, label, placeholder = "", required = false) => (
    <div>
      <label className={labelClass}>{label}{required && reqMark}</label>
      <textarea
        name={name} required={required} rows={4}
        placeholder={placeholder} value={form[name] || ""}
        onChange={handleChange} className={inputClass + " resize-none"}
      />
    </div>
  );

  const commonTopGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {field("fullName", "Full Name", "text", "John Doe", true)}
      {field("email", "Email Address", "email", "john@example.com", true)}
      <USPhoneInput name="phone" value={form.phone || ""} onChange={handleChange} label="Phone Number" required />
    </div>
  );

  const withPolicy = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {field("fullName", "Full Name", "text", "John Doe", true)}
      {field("email", "Email Address", "email", "john@example.com", true)}
      <USPhoneInput name="phone" value={form.phone || ""} onChange={handleChange} label="Phone Number" required />
      {field("policyNumber", "Policy Number", "text", "Policy #", true)}
    </div>
  );

  // ── Per-action form bodies ───────────────────────────────────────────
  const renderForm = () => {
    switch (action.key) {

      case "consultation":
        return (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("fullName", "Full Name", "text", "John Doe", true)}
              {field("email", "Email Address", "email", "john@example.com", true)}
              {field("address", "Address", "text", "Street address", true)}
              <USPhoneInput name="phone" value={form.phone || ""} onChange={handleChange} label="Phone Number" required />
            </div>
            {textarea("details", "Details on Consultation", "Share any context for your request")}
          </div>
        );

      case "coi":
        return (
          <div className="flex flex-col gap-4">
            {withPolicy()}
            {field("certificateHolder", "Certificate Holder Name / Company", "text", "Company or individual name", true)}
            {field("certificateHolderAddress", "Certificate Holder Address", "text", "Street address", true)}
            {field("additionalInsured", "Additional Insured", "text", "Name or company (if applicable)")}
            {select("deliveryMethod", "Delivery Method", DELIVERY_OPTS, true)}
            {textarea("notes", "Additional Notes", "Anything else we should know…")}
          </div>
        );

      case "policy-change":
        return (
          <div className="flex flex-col gap-4">
            {withPolicy()}
            {select("changeType", "Type of Change", CHANGE_TYPES, true)}
            {textarea("changeDescription", "Description of Change", "Please describe the change you need…", true)}
            {field("effectiveDate", "Requested Effective Date", "date")}
            {textarea("notes", "Additional Notes", "Anything else we should know…")}
          </div>
        );

      case "contact-update":
        return (
          <div className="flex flex-col gap-4">
            {withPolicy()}
            <div>
              <label className={labelClass}>Items to Update{reqMark}</label>
              <div className="flex flex-col gap-2 mt-1">
                {UPDATE_OPTIONS.map((opt) => {
                  const checked = (form.fieldsToUpdate || []).includes(opt);
                  return (
                    <label
                      key={opt}
                      className="flex items-center gap-3 cursor-pointer px-3 py-2.5 rounded-lg border transition-colors duration-200"
                      style={{
                        borderColor: checked ? "rgba(0,45,181,0.4)" : "rgba(0,0,0,0.1)",
                        backgroundColor: checked ? "rgba(0,45,181,0.04)" : "transparent",
                      }}
                    >
                      <input
                        type="checkbox" name="fieldsToUpdate" value={opt} checked={checked}
                        onChange={handleChange} className="w-4 h-4 accent-blue-dark flex-shrink-0"
                      />
                      <span className="font-constantia text-black-deep text-sm">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            {textarea("newInformation", "New Information", "Provide the updated details here…", true)}
            {textarea("notes", "Additional Notes", "Anything else we should know…")}
          </div>
        );

      case "claim":
        return (
          <div className="flex flex-col gap-4">
            {withPolicy()}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("dateOfLoss", "Date of Loss", "date", "", true)}
              {select("lossType", "Type of Loss", LOSS_TYPES, true)}
            </div>
            {field("lossLocation", "Location of Loss", "text", "Address or description of location", true)}
            {textarea("claimDescription", "Description of Loss", "Describe what happened in detail…", true)}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {select("injuries", "Were there any injuries?", YES_NO, true)}
              {select("otherParties", "Other parties involved?", YES_NO, true)}
            </div>
            {textarea("notes", "Additional Notes", "Any other relevant information…")}
          </div>
        );

      case "call-request":
        return (
          <div className="flex flex-col gap-4">
            {commonTopGrid()}
            {select("preferredCallTime", "Best Time to Call", CALL_TIMES, true)}
            {textarea("callReason", "Reason for Call", "What would you like to discuss?")}
          </div>
        );

      default:
        return null;
    }
  };

  const canSubmit = !submitting && !!form.fullName && !!form.email && !!form.phone;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white-pure rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-8 pt-7 pb-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <span
              className="inline-block font-cinzel uppercase tracking-widest border border-blue-dark border-opacity-30 text-blue-dark px-3 py-1 rounded-full mb-3"
              style={{ fontSize: "10px", letterSpacing: "0.2em" }}
            >
              {action.type}
            </span>
            <h2 className="font-cinzel font-bold text-blue-dark uppercase leading-snug" style={{ fontSize: "18px", letterSpacing: "0.05em" }}>
              {action.title}
            </h2>
            <p className="font-constantia text-black-deep mt-1" style={{ fontSize: "14px", opacity: 0.55 }}>
              {action.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-blue-dark hover:text-blue-dark transition-colors flex-shrink-0 mt-1"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 overflow-y-auto flex-1">
          {submitted ? (
            <div className="flex flex-col items-center text-center gap-4 py-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "16px", letterSpacing: "0.08em" }}>
                Request Received!
              </h3>
              <p className="font-constantia text-black-deep max-w-sm leading-relaxed" style={{ fontSize: "15px", opacity: 0.65 }}>
                Thank you, <strong>{form.fullName}</strong>. One of our licensed agents will follow up within 1–2 business days.
              </p>
              <button
                onClick={onClose}
                className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-3 rounded-full hover:bg-blue-primary transition-colors duration-200 mt-2"
                style={{ fontSize: "11px" }}
              >
                Close
              </button>
            </div>
          ) : (
            renderForm()
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="px-8 py-5 border-t border-gray-100 flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="font-cinzel uppercase tracking-widest text-black-deep border border-gray-200 px-6 py-2.5 rounded-full hover:border-blue-dark hover:text-blue-dark transition-colors duration-200"
              style={{ fontSize: "11px" }}
            >
              Cancel
            </button>
            <div className="flex flex-col items-end gap-1.5">
              {error && (
                <p className="font-constantia text-red-600 text-sm">{error}</p>
              )}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-2.5 rounded-full hover:bg-blue-primary transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontSize: "11px" }}
              >
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
